// AR → EN mapping for city + landmark names used across the booking system.
// Kept manual (not machine-translated) so official spellings match the tourism
// board's standard (e.g., "Makkah" not "Mecca", "Madinah" not "Medina").
//
// Usage:
//   import { translateLocation } from "@/utils/locationI18n";
//   const label = translateLocation(booking.pickup_location, i18n.language);
//
// If the string contains multiple tokens (e.g., "مطار الملك عبدالعزيز - جدة")
// we translate the longest recognized substring first, then fall through the
// dictionary word by word. Unrecognized Arabic tokens are returned as-is.

const AR_TO_EN = {
  // Cities
  "مكة المكرمة": "Makkah",
  "مكة": "Makkah",
  "المدينة المنورة": "Madinah",
  "المدينة": "Madinah",
  "جدة": "Jeddah",
  "الطائف": "Taif",
  "الرياض": "Riyadh",
  "الدمام": "Dammam",
  "ينبع": "Yanbu",
  "الأحساء": "Al Ahsa",
  "الخبر": "Khobar",
  "أبها": "Abha",
  "تبوك": "Tabuk",
  "حائل": "Hail",
  "بريدة": "Buraidah",
  "القصيم": "Al Qassim",

  // Airports
  "مطار الملك عبدالعزيز": "King Abdulaziz Airport",
  "مطار الملك عبدالعزيز الدولي": "King Abdulaziz Int'l Airport",
  "مطار الأمير محمد بن عبدالعزيز": "Prince Mohammed bin Abdulaziz Airport",
  "مطار الملك خالد": "King Khalid Airport",
  "مطار الطائف": "Taif Airport",
  "مطار الطائف الدولي": "Taif International Airport",
  "مطار جدة": "Jeddah Airport",
  "المطار": "Airport",

  // Train stations
  "محطة قطار جدة": "Jeddah Train Station",
  "محطة قطار الحرمين جدة": "Haramain Train Station — Jeddah",
  "محطة قطار المدينة": "Madinah Train Station",
  "محطة قطار الحرمين المدينة": "Haramain Train Station — Madinah",
  "محطة قطار مكة": "Makkah Train Station",
  "محطة قطار الحرمين مكة": "Haramain Train Station — Makkah",
  "محطة قطار الحرمين": "Haramain Train Station",
  "محطة القطار": "Train Station",
  "قطار الحرمين": "Haramain Train",
  "قطار": "Train",

  // Shrines / landmarks (destinations)
  "المسجد الحرام": "The Grand Mosque (Haram)",
  "المسجد النبوي": "The Prophet's Mosque",
  "الحرم المكي": "The Grand Mosque (Haram)",
  "الحرم النبوي": "The Prophet's Mosque",
  "منى": "Mina",
  "عرفات": "Arafat",
  "مزدلفة": "Muzdalifah",
  "الجعرانة": "Al Ja'ranah",
  "التنعيم": "Al Tan'eem",
  "الحديبية": "Al Hudaibiyah",
  "قباء": "Quba",
  "أحد": "Uhud",
  "جبل أحد": "Mount Uhud",
  "مسجد قباء": "Quba Mosque",
  "مسجد القبلتين": "Al Qiblatain Mosque",
  "البقيع": "Al Baqi",
  "مقبرة البقيع": "Al Baqi Cemetery",

  // Common connectors / preposition-like tokens
  " إلى ": " to ",
  " - ": " — ",
  "–": "—",
};

// Sort keys longest-first so multi-word phrases match before their sub-tokens.
const KEYS_SORTED = Object.keys(AR_TO_EN).sort((a, b) => b.length - a.length);

/**
 * Translate an Arabic location label to English.
 * When `lang` is not "en" (or the string has no Arabic tokens we know), returns
 * the input unchanged.
 */
export function translateLocation(raw, lang) {
  if (!raw || typeof raw !== "string") return raw;
  if (lang !== "en") return raw;
  let out = raw;
  for (const ar of KEYS_SORTED) {
    if (out.includes(ar)) {
      // Replace ALL occurrences.
      out = out.split(ar).join(AR_TO_EN[ar]);
    }
  }
  return out;
}
